/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { KpiTimePeriod, useOrgUnitKpiDataQuery } from "~/generated/graphql";
import { getKpiFetchConfig } from "~/components/KPI/utils";
import { KpiType } from "~/constants";
import { useCurrency } from "~/hooks";
import { mapOrgUnitKpi, mapWorstChildOrgUnits } from "../../mappers";

type Props = {
  orgUnitId: string;
  period: KpiTimePeriod;
  kpiType: KpiType;
  childOrgUnitId?: string;
};

export function useOrgUnitKpiData({
  orgUnitId,
  period,
  kpiType,
  childOrgUnitId,
}: Props) {
  const { currency } = useCurrency();

  const {
    data: kpiData,
    loading: kpiLoading,
    error: kpiError,
  } = useOrgUnitKpiDataQuery({
    variables: {
      id: orgUnitId,
      period,
      ...getKpiFetchConfig(kpiType),
    },
  });

  const mappedKpiData = React.useMemo(
    () => mapOrgUnitKpi(kpiType, kpiData),
    [kpiData, kpiType]
  );

  const mappedWorstChildOrgUnits = React.useMemo(
    () => mapWorstChildOrgUnits(currency, kpiData),
    [currency, kpiData]
  );
  const childOrgUnitName =
    mappedWorstChildOrgUnits?.find(({ nid }) => nid === childOrgUnitId)?.name ??
    "";

  return {
    mappedKpiData,
    mappedWorstChildOrgUnits,
    childOrgUnitName,
    kpiLoading,
    kpiError,
  };
}
